import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import AllergyMultiSelect from '../ui/allergy-multi-select';
import MedicalHistoryMultiSelect from '../ui/medical-history-multi-select';
import { PatientFormData } from './patientForm.types';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { HeartPulse, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

interface MedicalHistorySectionProps {
  form: UseFormReturn<PatientFormData>;
  isGuided?: boolean;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const MedicalHistorySection = ({
  form,
  isGuided = false,
  isOpen = true,
  onOpenChange,
}: MedicalHistorySectionProps) => {
  const hasErrors = Boolean(
    form.formState.errors.medication_allergies ||
    form.formState.errors.no_known_allergies ||
    form.formState.errors.medical_history
  );

  const content = (
    <div className='space-y-4'>
      <div className='space-y-4'>
        <div className='flex items-center space-x-2'>
          <Checkbox
            id='no-known-allergies'
            checked={form.watch('no_known_allergies') || false}
            onCheckedChange={checked => {
              const isChecked = checked as boolean;
              form.setValue('no_known_allergies', isChecked, { shouldValidate: true });
              if (isChecked) {
                // Clear allergies when "No Known Allergies" is checked
                form.setValue('medication_allergies', [], { shouldValidate: true });
              }
            }}
          />
          <Label htmlFor='no-known-allergies' className='cursor-pointer font-normal'>
            No Known Allergies <span className='text-red-500'>*</span>
          </Label>
        </div>
        {form.formState.errors.no_known_allergies && (
          <p className='text-[0.8rem] text-red-500'>
            {form.formState.errors.no_known_allergies.message}
          </p>
        )}

        {!form.watch('no_known_allergies') && (
          <div className='space-y-1.5'>
            <AllergyMultiSelect
              form={form}
              name='medication_allergies'
              label='Medication Allergies'
              placeholder='Search and select allergies...'
              required={true}
              disabled={false}
            />
            <p className='text-xs text-slate-500'>
              Search and select allergies from the NLM Clinical Tables database. Select "No Known
              Allergies" if the patient has no allergies.
            </p>
          </div>
        )}
      </div>

      <MedicalHistoryMultiSelect
        form={form}
        name='medical_history'
        label='Medical History'
        placeholder='Select medical conditions...'
      />
    </div>
  );

  return (
    <Collapsible open={isOpen} onOpenChange={onOpenChange}>
      <CollapsibleTrigger asChild>
        <button className="flex w-full items-center justify-between py-3 text-sm font-semibold text-slate-700 hover:text-slate-900">
          <span className="flex items-center gap-2">
            <HeartPulse className="h-4 w-4" />
            Medical History
            {hasErrors && <span className="h-2 w-2 rounded-full bg-red-500" />}
          </span>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400 font-normal">Required</span>
            <ChevronDown className={cn("h-4 w-4 transition-transform", isOpen && "rotate-180")} />
          </div>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className='space-y-4 pt-2'>
          {content}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default MedicalHistorySection;
